import React, { useEffect, useState } from 'react';
import EditDrawer from './EditDrawer';
import Field from '../../Field';
import PhoneField from '../../PhoneField';
import {
  CONTACT_NUMBERS, FAMILY_CONTACT_TEXT_MAX, FAMILY_NOTES_MAX,
  familyContactRecordErrors,
} from '../../../domain/people';
import s from './editStudent.module.css';
import SmsUnavailable, { smsOffClass } from '../../SmsUnavailable';
import { useSmsAvailable } from '../../../context/SmsGateContext';
import { smsShown } from '../../../domain/smsGate';

// Add or edit one of a family's contacts (#176), a record in the
// `familyContacts` collection (`normaliseFamilyContact` in domain/people.js).
//
// ── The fields are MMS's, in MMS's order (#176) ────────────────────────────
//
// Title, First Name, Last Name, Email Address, Mobile Number, Home Number,
// Work Number — each number with its own SMS Capable tick — Address, Private
// Note, then a Preferences block of four boxes. Everything except First and
// Last Name is captioned `Optional`, as there. MMS's Address also has a
// `Quick Select` that copies an address from another contact; this has none.
//
// The two reminder boxes carry MMS's caption: a contact's reminder flag is
// ANDed with the category's reminder setting, so the shape is contact ×
// category (`contactWantsReminder` in domain/notifications.js, #416).
export default function FamilyContactDrawer({
  open, contact = null, saving = false, onClose, onSave,
}) {
  const [form, setForm] = useState(null);
  const smsAvailable = useSmsAvailable();

  // Seeded when the drawer opens rather than on every render, so typing is not
  // fighting a prop. `contact` is null for Add, the same form with empty
  // values (#98), which is why there is one component here rather than two.
  useEffect(() => {
    if (!open) { setForm(null); return; }
    const c = contact || {};
    setForm({
      title: c.title || '',
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      email: c.email || '',
      address: c.address || '',
      note: c.note || '',
      mobile: c.mobile || '',
      home: c.home || '',
      work: c.work || '',
      mobileSmsCapable: Boolean(c.mobileSmsCapable),
      homeSmsCapable: Boolean(c.homeSmsCapable),
      workSmsCapable: Boolean(c.workSmsCapable),
      isInvoiceRecipient: Boolean(c.isInvoiceRecipient),
      emailReminders: Boolean(c.emailReminders),
      smsReminders: Boolean(c.smsReminders),
    });
  }, [open, contact]);

  const [attempted, setAttempted] = useState(false);
  useEffect(() => { if (open) setAttempted(false); }, [open]);

  if (!open || !form) return null;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  // Computed every render rather than stored, so a message cannot disagree
  // with the field it is under. Shown only after a Save has been tried, so a
  // half-typed email is not an error yet (#116).
  const errors = familyContactRecordErrors(form);
  const showing = attempted ? errors : {};

  const submit = () => {
    setAttempted(true);
    if (Object.keys(errors).length) return;
    onSave(form);
  };

  return (
    <EditDrawer
      open
      title="Family Contact"
      saving={saving}
      onClose={onClose}
      onSave={submit}
    >
      <div className={[s.spaced, s.narrow].join(' ')}>
        <Field label="Title" htmlFor="contact-title" hint="Optional">
          <input
            id="contact-title" className={s.input} maxLength={FAMILY_CONTACT_TEXT_MAX.title}
            value={form.title} onChange={(e) => set('title', e.target.value)}
          />
        </Field>
      </div>

      <div className={s.pair}>
        <Field label="First Name" htmlFor="contact-first" required error={showing.firstName}>
          <input
            id="contact-first" className={s.input} maxLength={FAMILY_CONTACT_TEXT_MAX.firstName}
            value={form.firstName} onChange={(e) => set('firstName', e.target.value)}
          />
        </Field>
        <Field label="Last Name" htmlFor="contact-last" required error={showing.lastName}>
          <input
            id="contact-last" className={s.input} maxLength={FAMILY_CONTACT_TEXT_MAX.lastName}
            value={form.lastName} onChange={(e) => set('lastName', e.target.value)}
          />
        </Field>
      </div>

      <div className={[s.spaced, s.medium].join(' ')}>
        <Field label="Email Address" htmlFor="contact-email" hint="Optional" error={showing.email}>
          <input
            id="contact-email" type="email" className={s.input}
            maxLength={FAMILY_CONTACT_TEXT_MAX.email}
            value={form.email} onChange={(e) => set('email', e.target.value)}
          />
        </Field>
      </div>

      {/* Three numbers, walked from CONTACT_NUMBERS rather than written out,
          so the drawer, the card, the normaliser and the refusal cannot end up
          knowing about different numbers. */}
      {CONTACT_NUMBERS.map(({ key, sms, label }) => (
        <div key={key} className={[s.spaced, s.medium].join(' ')}>
          <PhoneField
            id={`contact-${key}`}
            label={label}
            value={form[key]}
            error={showing[key]}
            onChange={(v) => set(key, v)}
          />
          <label className={s.checkRow}>
            <input
              type="checkbox" checked={form[sms]}
              onChange={(e) => set(sms, e.target.checked)}
            />
            <span>SMS Capable</span>
          </label>
        </div>
      ))}

      <div className={[s.spaced, s.medium].join(' ')}>
        <Field label="Address" htmlFor="contact-address" hint="Optional">
          <input
            id="contact-address" className={s.input} maxLength={FAMILY_CONTACT_TEXT_MAX.address}
            value={form.address} onChange={(e) => set('address', e.target.value)}
          />
        </Field>
      </div>

      {/* `FAMILY_NOTES_MAX` (#92) bounds it. MMS puts it on the contact as
          Private Note, so it lands here rather than on the family. */}
      <div className={[s.spaced, s.medium].join(' ')}>
        <Field label="Private Note" htmlFor="contact-note" hint="Optional" error={showing.note}>
          <textarea
            id="contact-note" className={s.textarea} rows={3} maxLength={FAMILY_NOTES_MAX}
            value={form.note} onChange={(e) => set('note', e.target.value)}
          />
        </Field>
      </div>

      <div className={s.groupLabelSpaced}>Preferences</div>
      <label className={s.checkRow}>
        <input
          type="checkbox" checked={form.isInvoiceRecipient}
          onChange={(e) => set('isInvoiceRecipient', e.target.checked)}
        />
        <span>Set as Preferred Invoice Recipient</span>
      </label>
      {/* MMS also has `Show in Student Portal contacts` here; this drawer
          stores no such field (#417). */}
      <label className={s.checkRow}>
        <input
          type="checkbox" checked={form.emailReminders}
          onChange={(e) => set('emailReminders', e.target.checked)}
        />
        <span>Send email lesson reminders</span>
      </label>
      {/* Disabled and drawn off while texts cannot be sent (#782,
          domain/smsGate.js); a saved tick is kept as it was. */}
      <label className={smsAvailable ? s.checkRow : `${s.checkRow} ${smsOffClass}`}>
        <input
          type="checkbox" checked={smsShown(form.smsReminders, smsAvailable)}
          disabled={!smsAvailable}
          onChange={(e) => { if (smsAvailable) set('smsReminders', e.target.checked); }}
        />
        <span>Send SMS lesson reminders</span>
      </label>
      {smsAvailable ? null : <SmsUnavailable />}
      {/* MMS's own caption, verbatim, because it is the specification: the
          tick above is one half of an AND, and the other half is the
          category's reminder lead (domain/categories.js). The join is
          `contactWantsReminder` in domain/notifications.js, read by the sweep
          in domain/reminders.js (#416). */}
      <p className={s.help}>
        This family contact will only receive reminders for categories with
        reminders enabled. To manage them, go to Categories &amp; Locations.
      </p>
    </EditDrawer>
  );
}
